<template>
  <div :style="containerStyle">
    <HeaderLargeLight>
      Workspace Setting
    </HeaderLargeLight>
    <CardSecondary :style="cardStyle">
      <div :style="tenantNameContainerStyle">
        <div :style="tenantNameInnerContainerStyle">
          <InputTextPrimary v-model="newTenantName"
                            :disabled="!enableTenantNameForm"
                            :style="inputStyle">
            <template v-slot:label>
              Workspace name
            </template>
          </InputTextPrimary>
        </div>
        <div :style="tenantNameButtonContainerStyle">
          <template v-if="enableTenantNameForm">
            <ButtonSecondary :style="tenantNameButtonStyle"
                             @click="cancelChangeTenantName">
              Cancel
            </ButtonSecondary>
          </template>
          <template v-else>
            <ButtonSecondary :style="tenantNameButtonStyle"
                             @click="() => { enableTenantNameForm = true; }">
              Change
            </ButtonSecondary>
          </template>
        </div>
      </div>
      <div :style="tenantAccountIdContainerStyle">
        <LabelPrimary>
          Workspace ID
        </LabelPrimary>
        <div :style="tenantAccountIdStyle">
          {{ tenantAccount.accountId }}
        </div>
      </div>
      <div :style="tenantDescriptionContainerStyle">
        <InputTextPrimary v-model="newDescription"
                          :style="inputStyle">
          <template v-slot:label>
            Workspace description
          </template>
        </InputTextPrimary>
      </div>
      <div :style="buttonsContainerStyle">
        <ButtonWarning :style="deleteButtonStyle"
                       @click="openModalToDeleteWorkspace">
          Delete Workspace
        </ButtonWarning>
        <div>
          <ButtonSecondary :style="cancelButtonStyle"
                           @click="cancelUpdate">
            Cancel
          </ButtonSecondary>
          <ButtonPrimary :disabled="!updateButtonEnabled"
                         :style="updateButtonStyle"
                         @click="update">
            Update
          </ButtonPrimary>
        </div>
      </div>
    </CardSecondary>
  </div>
</template>

<script>
  import ApiTenantAccount from '@js/api/tenant-account'
  import ButtonPrimary from '@modules/button/button-primary'
  import ButtonSecondary from '@modules/button/button-secondary'
  import ButtonWarning from '@modules/button/button-warning'
  import CardSecondary from '@modules/card/card-secondary'
  import HeaderLargeLight from '@modules/text/header-large-light'
  import InputTextPrimary from '@modules/input/input-text-primary'
  import LabelPrimary from '@modules/input/label-primary'
  import { FontStyle, Layout } from '@js/styles'
  import { INPUT_MAX_LENGTHS, INPUT_MIN_LENGTHS } from '@js/constants'
  import { mapGetters, mapState } from 'vuex'
  import { MODAL_WINDOW_NAMES } from '@js/constants'

  export default {
    name: 'AdminWorkspaceView',
    components: {
      ButtonPrimary,
      ButtonSecondary,
      ButtonWarning,
      CardSecondary,
      HeaderLargeLight,
      InputTextPrimary,
      LabelPrimary,
    },
    data: function () {
      return {
        buttonsContainerStyle: {
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          margin: '40px 0 0 0',
        },
        cancelButtonStyle: {
          margin: `${Layout.space.sm} ${Layout.space.md} 0 0`,
          minWidth: '80px',
        },
        cardStyle: {
          marginTop: Layout.space.lg,
          padding: Layout.space.lg,
        },
        containerStyle: {
          maxWidth: '608px',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: `0 ${Layout.space.lg}`,
          width: '100%',
        },
        deleteButtonStyle: {
          margin: `${Layout.space.sm} 0 0 0`,
          minWidth: '160px',
        },
        enableTenantNameForm: false,
        inputStyle: {
          width: '100%',
        },
        tenantAccount: {},
        tenantAccountIdContainerStyle: {
          margin: `${Layout.space.lg} 0 0 0`,
        },
        tenantAccountIdStyle: {
          fontSize: FontStyle.size.md,
          fontWeight: FontStyle.weight.md,
        },
        newTenantName: '',
        newDescription: '',
        tenantDescriptionContainerStyle: {
          margin: `${Layout.space.lg} 0 0 0`,
        },
        tenantNameButtonContainerStyle: {
          marginLeft: Layout.space.lg,
        },
        tenantNameButtonStyle: {
          width: '86px',
          minWidth: '86px',
        },
        tenantNameContainerStyle: {
          alignItems: 'flex-end',
          display: 'flex',
          justifyContent: 'space-between',
        },
        tenantNameInnerContainerStyle: {
          width: '100%',
        },
        updateButtonStyle: {
          margin: `${Layout.space.sm} 0 0 0`,
          minWidth: '86px',
        },
      };
    },
    created: async function () {
      this.newTenantName = this.tenant.name;
      this.newDescription = this.tenant.description;

      if (this.isAdmin) {
        await this.fetchTenantAccount();
      }
    },
    computed: {
      ...mapGetters('tenantUser', ['isAdmin']),
      ...mapState({
        tenant: state => state.tenant,
        tenantName: state => state.tenant.name,
        tenantDescription: state => state.tenant.description,
      }),
      updateButtonEnabled: function () {
        if (!this.isValidTenantName) {
          return false;
        }

        const nameUpdated = this.newTenantName !== this.tenant.name;
        const descriptionUpdated = this.newDescription != this.tenant.description;
        return nameUpdated || descriptionUpdated;
      },
      isValidTenantName: function () {
        const length = this.newTenantName.trim().length;
        return (length >= INPUT_MIN_LENGTHS.TENANT_NAME) && (length <= INPUT_MAX_LENGTHS.TENANT_NAME);
      },
    },
    methods: {
      cancelChangeTenantName: function () {
        this.newTenantName = this.tenant.name;
        this.enableTenantNameForm = false;
      },
      cancelUpdate: function () {
        this.resetInputs();
      },
      fetchTenantAccount: async function () {
        try {
          const api = await new ApiTenantAccount();
          const res = await api.get();
          this.tenantAccount = await res.data;
        } catch (error) {
          console.error(error);
          this.accountId = 'Failed to load Workspace ID. Contact Zipteam customer success.';
          this.$store.commit('notificaiton/setWarning', 'Failed to account info');
        }
      },
      openModalToDeleteWorkspace: function () {
        this.$modal.show(MODAL_WINDOW_NAMES.DELETE_TENANT_PRIMARY);
      },
      resetInputs: function () {
        this.newTenantName = this.tenant.name;
        this.newDescription = this.tenant.description;
        this.enableTenantNameForm = false;
      },
      update: async function () {
        try {
          const payload = {
            name: this.newTenantName,
            description: this.newDescription,
          };
          await this.$store.dispatch('tenant/updateTenantName', payload);
          await this.$store.commit('notification/setMessage', 'Updated workspace data');
          await this.resetInputs();
        } catch (error) {
          this.$store.commit('notification/setWarning', 'Failed to update');
          this.resetInputs();
          console.error(error);
        }
      },
    },
  };
</script>
